import React, {useState, useEffect, useRef} from 'react';
import {useMutation} from 'react-query';

import {useAxios} from 'src/utils/http';

interface BatchCreatePartResult {
  part_index: number;
  name: string;
  dataset_id?: number;
  study_count: number;
  skipped?: boolean;
}

interface PreviewResponse {
  prefix: string;
  csv_hash: string;
  total_studies: number;
  total_parts_expected: number;
  missing_study_ids: string[] | null;
  valid: boolean;
  parts: BatchCreatePartResult[];
  metadata_ready_count: number;
  metadata_missing_count: number;
  metadata_missing_samples?: string[];
}

interface CreateResponse {
  prefix: string;
  csv_hash: string;
  total_studies: number;
  total_parts_expected: number;
  created_datasets: BatchCreatePartResult[];
  skipped_parts: number[];
  errors: string[];
}

const STATUS_OPTIONS = [
  {
    value: 'dataset_created',
    label: 'Created at rest — submit later in waves (recommended)',
  },
  {
    value: 'metadata_cust_requested',
    label: 'Start pipeline immediately — skips the Batch Submit click',
  },
];

export interface NewFromCSVModalProps {
  onClose: () => void;
  onCreated: (prefix: string) => void;
}

export const NewFromCSVModal = ({onClose, onCreated}: NewFromCSVModalProps) => {
  const api = useAxios();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [prefix, setPrefix] = useState('');
  const [perDataset, setPerDataset] = useState('500');
  const [status, setStatus] = useState('dataset_created');
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const isFormValid =
    file !== null &&
    prefix.trim().length >= 4 &&
    parseInt(perDataset, 10) > 0 &&
    parseInt(perDataset, 10) <= 2000;

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('csv', file as Blob);
    fd.append('prefix', prefix.trim());
    fd.append('per_dataset', perDataset);
    fd.append('status', status);
    return fd;
  };

  const previewMutation = useMutation<PreviewResponse, Error, void>(
    () =>
      api
        .post(
          '/v1/admin/datasets/batch-create-from-csv/preview',
          buildFormData(),
          {headers: {'Content-Type': 'multipart/form-data'}}
        )
        .then(res => res.data as PreviewResponse),
    {
      onSuccess: data => {
        setPreview(data);
        setConfirming(false);
      },
    }
  );

  const createMutation = useMutation<CreateResponse, Error, void>(
    () =>
      api
        .post('/v1/admin/datasets/batch-create-from-csv', buildFormData(), {
          headers: {'Content-Type': 'multipart/form-data'},
        })
        .then(res => res.data as CreateResponse),
    {
      onSuccess: data => {
        if (data.errors.length === 0 && data.created_datasets.length > 0) {
          onCreated(data.prefix);
        }
      },
    }
  );

  const errorMessage = (err: unknown): string => {
    const e = err as {response?: {data?: {message?: string}}; message?: string};
    return e?.response?.data?.message || e?.message || 'Request failed';
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-lg">
          <div>
            <div className="text-lg font-bold text-gray-900">
              Create Batch from CSV
            </div>
            <div className="text-xs text-gray-500">
              Upload a CSV of study IDs — we create N datasets under one prefix.
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* File input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              CSV file (one study_id per line, header row optional)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              className="block w-full text-sm"
              onChange={e => {
                const f = e.target.files?.[0] ?? null;
                setFile(f);
                setPreview(null);
              }}
            />
            {file && (
              <div className="text-xs text-gray-500 mt-1">
                {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </div>
            )}
          </div>

          {/* Prefix */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dataset name prefix
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="e.g. PM_CT_MR_FM_Q226_green_verified_ken2399"
              value={prefix}
              onChange={e => {
                setPrefix(e.target.value);
                setPreview(null);
              }}
            />
            <p className="text-xs text-gray-400 mt-1">
              Datasets named <code>{'{prefix}_part_1'}</code>,{' '}
              <code>{'{prefix}_part_2'}</code>, etc. Min 4 chars.
            </p>
          </div>

          {/* Per dataset */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Studies per dataset
            </label>
            <input
              type="number"
              className="w-32 border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={perDataset}
              min={1}
              max={2000}
              onChange={e => {
                setPerDataset(e.target.value);
                setPreview(null);
              }}
            />
            <p className="text-xs text-gray-400 mt-1">
              Typical: 500. Openda chokes on datasets much larger than this.
            </p>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Starting status
            </label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={status}
              onChange={e => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 pt-2">
            <button
              className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50"
              disabled={!isFormValid || previewMutation.isLoading}
              onClick={() => previewMutation.mutate()}
            >
              {previewMutation.isLoading ? 'Validating…' : 'Preview'}
            </button>
            <button
              className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              disabled={
                !isFormValid ||
                createMutation.isLoading ||
                preview === null ||
                !preview.valid
              }
              onClick={() => setConfirming(true)}
            >
              Create{' '}
              {preview ? `(${preview.total_parts_expected} datasets)` : ''}
            </button>
          </div>

          {previewMutation.isError && (
            <ErrorBox>{errorMessage(previewMutation.error)}</ErrorBox>
          )}

          {/* Preview display */}
          {preview && <PreviewDisplay preview={preview} />}

          {/* Confirmation */}
          {confirming && preview && preview.valid && (
            <ConfirmCreateBox
              preview={preview}
              status={status}
              loading={createMutation.isLoading}
              onCancel={() => setConfirming(false)}
              onConfirm={() => createMutation.mutate()}
            />
          )}

          {createMutation.isError && (
            <ErrorBox>{errorMessage(createMutation.error)}</ErrorBox>
          )}

          {createMutation.data && (
            <CreateResultBox result={createMutation.data} onClose={onClose} />
          )}
        </div>
      </div>
    </div>
  );
};

const PreviewDisplay = ({preview}: {preview: PreviewResponse}) => (
  <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
    <div className="text-sm font-semibold text-gray-900 mb-3">Preview</div>
    <div className="grid grid-cols-3 gap-3 mb-3">
      <Stat
        label="Total studies"
        value={preview.total_studies.toLocaleString()}
      />
      <Stat label="Datasets" value={preview.total_parts_expected} />
      <Stat
        label="Validation"
        value={preview.valid ? 'Passed' : 'Failed'}
        tone={preview.valid ? 'success' : 'error'}
      />
    </div>

    {preview.missing_study_ids && preview.missing_study_ids.length > 0 && (
      <div className="p-3 bg-red-50 border border-red-200 rounded-md mt-2">
        <div className="text-sm font-medium text-red-800">
          {preview.missing_study_ids.length} study IDs not found in rdb:
        </div>
        <div className="text-xs text-red-700 mt-1 max-h-32 overflow-y-auto font-mono">
          {preview.missing_study_ids.slice(0, 50).map(id => (
            <div key={id}>{id}</div>
          ))}
          {preview.missing_study_ids.length > 50 && (
            <div className="text-red-500">
              … and {preview.missing_study_ids.length - 50} more
            </div>
          )}
        </div>
      </div>
    )}

    {preview.metadata_missing_count > 0 && (
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-md mt-2">
        <div className="text-sm font-medium text-amber-900">
          {preview.metadata_missing_count} of {preview.total_studies} studies
          lack <code>meta_data</code> rows ({preview.metadata_ready_count}{' '}
          ready)
        </div>
        <div className="text-xs text-amber-800 mt-1">
          After Batch Submit these datasets will flip to{' '}
          <code>metadata_cust_requested</code> but may stall at{' '}
          <code>metadata_dp_requested</code> — the delivery-master threshold is
          90%. Not blocking; the metadata fetch DAG may populate{' '}
          <code>meta_data</code> asynchronously.
        </div>
      </div>
    )}

    <div className="text-xs text-gray-400 mt-3">
      CSV hash: <code>{preview.csv_hash}</code>
    </div>
  </div>
);

const Stat = ({
  label,
  value,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  tone?: 'success' | 'error';
}) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div
      className={`text-xl font-bold ${
        tone === 'success'
          ? 'text-green-700'
          : tone === 'error'
          ? 'text-red-700'
          : 'text-gray-900'
      }`}
    >
      {value}
    </div>
  </div>
);

const ConfirmCreateBox = ({
  preview,
  status,
  loading,
  onCancel,
  onConfirm,
}: {
  preview: PreviewResponse;
  status: string;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => (
  <div className="p-3 bg-amber-50 border border-amber-300 rounded-md">
    <div className="text-sm font-medium text-amber-900">
      Create {preview.total_parts_expected} datasets from{' '}
      {preview.total_studies} studies?
    </div>
    <div className="text-xs text-amber-800 mt-1">
      Starting status: <code>{status}</code>
      {preview.metadata_missing_count > 0 && (
        <>
          {' '}
          · {preview.metadata_missing_count} studies lack <code>meta_data</code>{' '}
          and may stall.
        </>
      )}
    </div>
    <div className="flex gap-2 mt-3">
      <button
        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50"
        onClick={onCancel}
        disabled={loading}
      >
        Cancel
      </button>
      <button
        className="px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700"
        onClick={onConfirm}
        disabled={loading}
      >
        {loading ? 'Creating…' : 'Confirm & Create'}
      </button>
    </div>
  </div>
);

const CreateResultBox = ({
  result,
  onClose,
}: {
  result: CreateResponse;
  onClose: () => void;
}) => {
  const created = result.created_datasets.filter(d => !d.skipped).length;
  const skipped = result.skipped_parts.length;
  return (
    <div className="p-3 bg-green-50 border border-green-200 rounded-md">
      <div className="text-sm font-semibold text-green-900">
        Created {created} dataset(s)
      </div>
      {skipped > 0 && (
        <div className="text-xs text-green-800 mt-1">
          {skipped} part(s) skipped (already existed under this prefix).
        </div>
      )}
      {result.errors.length > 0 && (
        <div className="mt-2 text-xs text-red-700">
          {result.errors.length} error(s):{' '}
          {result.errors.slice(0, 2).join('; ')}
        </div>
      )}
      <div className="mt-3">
        <button
          className="px-3 py-1.5 text-sm rounded-md bg-green-600 text-white hover:bg-green-700"
          onClick={onClose}
        >
          Done — track this prefix
        </button>
      </div>
    </div>
  );
};

const ErrorBox = ({children}: {children: React.ReactNode}) => (
  <div className="p-3 bg-red-50 border border-red-200 rounded-md">
    <div className="text-sm font-medium text-red-800">Error</div>
    <div className="text-sm text-red-700 mt-1">{children}</div>
  </div>
);
